import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Workflow step: transform `rexel_initinfc` (fileVariant=initinfc) raw rows
 * into downstream domain tables.
 *
 * Stage 2 of the INITINFC ingest pipeline. Runs after `master_data_bulk_insert`
 * has populated `rexel_initinfc`. For each row in the latest batch:
 *   - Creates `im_products_v2` entries for new SKUs
 *   - Creates default `im_product_variants` for each new product
 *   - Updates metadata on existing products when source data has changed
 *   - Backfills `SupplierProduct` rows from GIPARENT1 primary-vendor links
 *   - Refreshes `stock_status` + buyer_code metadata on `im_inventory_policies`
 *     (pairs with gsnetdefTransform, which owns min/max on the same rows)
 *   - Does NOT touch warehouses or hierarchy (one-time setup only)
 *
 * Scoping: `updatedAfter` (typically wired from upstream `startedAt`) limits
 * the rexel_initinfc rows considered to those touched by the current run.
 */
export const transformInitinfcProductsStep = defineStepConfig({
  // NOTE: `type` is intentionally stable as "transform_initinfc_products"
  // even though this step now also writes supplier-products and inventory
  // policies. Persisted WorkflowDefinition rows reference this key.
  type: "transform_initinfc_products",
  name: "Transform INITINFC → Products, Suppliers & Policies",
  description:
    "Reads rexel_initinfc rows (fileVariant=initinfc) updated by the current run. Creates/updates im_products_v2 entries + default variants, backfills SupplierProduct from GIPARENT1, and refreshes stock_status + buyer metadata on im_inventory_policies (min/max stay owned by gsnetdefTransform).",
  category: "Transformation",
  libraryModule: "Procurement",
  inputSchema: z.object({
    updatedAfter: z
      .string()
      .optional()
      .describe(
        "ISO timestamp — only process rows updated after this time. " +
          "Typically wired from the upstream master_data_bulk_insert's startedAt output.",
      ),
  }),
  outputSchema: z.object({
    productsCreated: z
      .number()
      .describe("Number of new im_products_v2 rows created"),
    productsAlreadyPresent: z
      .number()
      .describe(
        "Number of products that already existed in im_products_v2 (not re-created)",
      ),
    variantsCreated: z
      .number()
      .describe(
        "Number of default im_product_variants rows created for new products",
      ),
    metadataUpdated: z
      .number()
      .describe(
        "Number of existing im_products_v2 rows whose metadata was refreshed from source",
      ),
    policiesUpserted: z
      .number()
      .describe(
        "Number of im_inventory_policies rows whose stock_status/metadata was refreshed from INITINFC",
      ),
    policyRowsScanned: z
      .number()
      .describe(
        "Number of rexel_initinfc rows scanned during the policies-refresh pass",
      ),
    updatedAfter: z
      .string()
      .optional()
      .describe(
        "Pass-through of the input timestamp so downstream steps can scope to the current run.",
      ),
  }),
  configSchema: z.object({
    batchSize: z
      .number()
      .min(1)
      .max(5000)
      .prefault(500)
      .describe("Number of rows to process per batch"),
  }),
  emits: [] as const,
  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
